import styled from "@emotion/styled";
import { Button, Grid } from "@mui/material";
import { Aligned, VerticalSpacer } from "components/Layout";
import { get, post } from "lib/fetch";
import { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Rule } from "./Rule";
import { DragDropContext, Draggable, Droppable } from "@hello-pangea/dnd";
import { RuleHeader } from "./RuleHeader";
import { cloneDeep, isEmpty } from "lodash";
import download from "../../../components/content/icons/download.svg";
import greenTick from "../../../components/content/icons/green-tick.svg";
import { CancelRulesDialog } from "./CancelRulesDialog";
import {
  createCsvData,
  getBlankRule,
  isLatestVersion,
  prepareRulesForDisplay,
  prepareRulesForUpload,
  validateRules,
} from "./utils";
import { setSubmittingChanges } from "actions/submittingChanges";
import { IState, IStructuredError } from "store/types";
import Spinner from "components/spinner";
import { StringParam, useQueryParams } from "use-query-params";
import { RuleErrorsDialog } from "./RuleErrorsDialog";
import { openSnackBarWithSuccess } from "actions/snackBar";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";

const RulesTitle = styled.p`
  font-family: "Roboto";
  font-style: "normal";
  font-weight: 400;
  font-size: 31px;
  line-height: 31px;
  margin: 0px;
  color: #252525;
`;

const RulesVersion = styled.p`
  font-family: "Roboto";
  font-style: "normal";
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  margin: 0px;
  color: #252525;
`;

const ContentPanel = styled.div`
  flex: 1 0 auto;
  padding-left: 24px;
  padding-right: 24px;
`;

const Footer = styled.footer`
  width: 100%;
  padding-top: 20px;
  padding-bottom: 20px;
  border-top: 1px solid black;
  flex-shrink: 0;
  margin-top: 0px;
  position: fixed;
  bottom: 0px;
`;

const DroppableContainer = styled.div<{ isdraggingover?: string; sidebar?: string }>`
  padding-left: ${(props) => (props.sidebar === "true" ? "2px" : "2px")};
  padding-right: ${(props) => (props.sidebar === "true" ? "2px" : "2px")};
  padding-top: 6px;
  padding-bottom: 6px;
  transition: background-color 0.2s ease;
  border: ${(props) => (props.isdraggingover === "true" ? "1px dashed #9747FF" : "1px solid white")};
  border-radius: 5px;
  width: 100%;
`;

const DraggableContainer = styled.div<{ isdragging?: string }>`
  border: ${(props) => (props.isdragging === "true" ? "2px solid #252525" : "1px solid #999999")};
  border-radius: 4px;
  padding: 0px;
  margin-left: 0px;
  margin-bottom: 8px;
  transition: background-color 0.2s ease;
  background-color: ${(props) => (props.isdragging === "true" ? "#eeeeee" : "white")};
  width: 100%;
`;

interface IProps {
  version: string;
  onCancel: () => any;
}

export interface RuleData {
  checked: boolean;
  rid: number;
  type: string;
  rule: string;
  sourceMatchScript: string;
  targetMatchScript: string;
  action: string;
}

export const RuleEditor = ({ version, onCancel }: IProps) => {
  const submittingChanges = useSelector((state: IState) => state.submittingChanges);
  const config = useSelector((state: IState) => state.config);
  const isEditable = isLatestVersion(config, version);

  // Query parameters
  const [query, _setQuery] = useQueryParams({
    active: StringParam,
  });
  const [changesMade, setChangesMade] = useState(false);
  const [uploadResponse, setUploadResponse] = useState<IStructuredError[]>([]);
  const [showDialog, setShowDialog] = useState(false);
  const [allChecked, setAllChecked] = useState(false);
  const [data, setData] = useState<RuleData[]>([]);
  const dispatch = useDispatch();
  useEffect(() => {
    const loadRule = async () => {
      try {
        dispatch(setSubmittingChanges(true));
        setData([]);
        setAllChecked(false);
        setChangesMade(false);
        const rules: any = await get(`/v1/rules?version=${version}&format=json`);
        setData(prepareRulesForDisplay(rules));
      } catch (e) {
        console.log(e);
      } finally {
        dispatch(setSubmittingChanges(false));
      }
    };
    loadRule();
  }, [dispatch, setChangesMade, version]);

  // Track window height changes
  const [height, setHeight] = useState(window.innerHeight);
  const updateHeight = useCallback((offset: number) => {
    setHeight(window.innerHeight - offset);
  }, []);
  useEffect(() => {
    const checkCount = !isEmpty(data) ? data.filter((d) => d.checked).length : 0;
    const newHeight = 305 + Number(checkCount > 0 ? 60 : 0);
    updateHeight(newHeight);
    window.addEventListener("resize", () => updateHeight(newHeight));
    return () => window.removeEventListener("resize", () => updateHeight(newHeight));
  }, [updateHeight, data]);

  const modifyRule = useCallback(
    (rule: RuleData) => {
      const modifyIndex = data.findIndex((d: RuleData) => d.rid === rule.rid);
      if (modifyIndex >= 0) {
        if (data[modifyIndex].checked != rule.checked) {
          setAllChecked(false);
        }
        const modifyRule = { ...rule };
        const newData = [...data.slice(0, modifyIndex), modifyRule, ...data.slice(modifyIndex + 1)];
        setData(newData);
        setChangesMade(true);
      }
    },
    [data, setChangesMade],
  );

  const onDragEnd = (result: any) => {
    const { destination, source, _draggableId } = result;
    if (!destination || (destination.droppableId === source.droppableId && destination.index === source.index)) {
      return;
    }
    const newData = [...data];
    const [removed] = newData.splice(source.index, 1);
    newData.splice(destination.index, 0, removed);
    setData(newData);
    setChangesMade(true);
  };

  const addRule = (position: number) => {
    setChangesMade(true);
    setData((data) => {
      let nextRid = 1;
      if (data.length > 0) {
        nextRid = Math.max(...data.map((d) => d.rid)) + 1;
      }
      if (position === 0) {
        const newData = [getBlankRule(nextRid), ...data];
        return newData;
      } else {
        const newData = [...data, getBlankRule(nextRid)];
        return newData;
      }
    });
  };

  const addRuleAtPosition = (rule: RuleData, position: number) => {
    setChangesMade(true);
    setData((data) => {
      const nextRid = Math.max(...data.map((d) => d.rid)) + 1;
      const index = data.findIndex((r) => r.rid === rule.rid);
      const newData = [...data.slice(0, index + position), getBlankRule(nextRid), ...data.slice(index + position)];
      return newData;
    });
  };

  const deleteRules = () => {
    setChangesMade(true);
    setData((data) => {
      const newData = [...data.filter((d) => !d.checked)];
      return newData;
    });
  };

  const deleteRule = (rule: RuleData) => {
    setChangesMade(true);
    setData((data) => {
      const newData = [...data].filter((r) => r.rid !== rule.rid);
      return newData;
    });
  };

  const checkAllRules = () => {
    setData((data) => {
      const newData = [...data];
      newData.forEach((d) => {
        d.checked = !allChecked;
      });
      return newData;
    });
    setAllChecked(!allChecked);
  };

  const checkedRules = !isEmpty(data) ? data.filter((d) => d.checked).length : 0;

  const onDownloadCSV = useCallback(() => {
    try {
      const csvData = cloneDeep(data);
      const blob = new Blob([createCsvData(csvData)]);
      const fileDownloadUrl = URL.createObjectURL(blob);
      const anchorTag = document.createElement("a");
      anchorTag.download = `rules_${version}.csv`;
      anchorTag.href = fileDownloadUrl;
      anchorTag.click();
    } catch (e) {
      console.log(e);
    }
  }, [data, version]);

  const onCancelButtonClick = () => {
    if (changesMade) {
      setShowDialog(true);
    } else {
      onCancel();
    }
  };
  const onNavigateBack = () => {
    onCancel();
  };
  const onSaveButtonClick = async () => {
    try {
      const errors = validateRules(data);
      if (isEmpty(errors)) {
        const payload = prepareRulesForUpload(cloneDeep(data));
        await post(`/v1/rules?version=${version}&format=jsonbody`, payload, true);
        dispatch(openSnackBarWithSuccess("A new version of rules has been saved successfully"));
        onCancel();
      } else {
        setUploadResponse(errors);
      }
    } catch (e: any) {
      console.log(e);
      let errors = [] as IStructuredError[];
      // use the default errors, but if errorsStructured is present use that.
      if (!isEmpty(e.payload.errors) && isEmpty(e.payload.errorsStructured)) {
        errors.push({ line: -1, error: e.payload.errors[0], rid: 0 });
      } else if (!isEmpty(e.payload.errorsStructured)) {
        errors = e.payload.errorsStructured;
      } else {
        errors.push({ line: -1, error: "An unknown error occured", rid: 0 });
      }
      setUploadResponse(errors);
    }
  };

  if (submittingChanges) {
    return <Spinner loading={submittingChanges} />;
  } else {
    return (
      <>
        <ContentPanel>
          <VerticalSpacer height={"24px"} />
          <Grid container>
            <Grid item xs={3} style={{ display: "flex", justifyContent: "left", alignItems: "center" }}>
              <div>
                <RulesTitle>Rules</RulesTitle>
                <RulesVersion>Version: {version}</RulesVersion>
              </div>
            </Grid>
            <Grid item xs={5} style={{ display: "flex", justifyContent: "left", alignItems: "center" }}>
              <ErrorOutlineIcon style={{ fontSize: "large", marginTop: -2, paddingRight: 2, color: "#252525" }} />
              <RulesVersion>Rules are executed in the order they appear below</RulesVersion>
            </Grid>
            <Grid item xs={4} style={{ display: "flex", justifyContent: "right", alignItems: "center" }}>
              {query.active === "true" && (
                <Aligned>
                  <img src={greenTick} alt="" height="18px" />
                  <RulesVersion>&nbsp;Active version</RulesVersion>
                </Aligned>
              )}
              &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
              <Button variant="outlined" onClick={() => onDownloadCSV()}>
                <img src={download} alt="" height="18px" />
                &nbsp; CSV
              </Button>
              &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
              <Button variant="contained" onClick={() => addRule(0)}>
                + New rule
              </Button>
            </Grid>
            <Grid item xs={12}>
              <VerticalSpacer height={"12px"} />
            </Grid>
          </Grid>
          <RuleHeader
            onCheckAll={checkAllRules}
            checked={allChecked}
            onDelete={deleteRules}
            checkCount={checkedRules}
          />
          <DragDropContext onDragEnd={onDragEnd}>
            <Grid
              container
              style={{
                overflowY: "scroll",
                maxHeight: height,
                alignItems: "center",
              }}
            >
              <Droppable droppableId="rules">
                {(provided, snapshot) => (
                  <DroppableContainer
                    {...provided.droppableProps}
                    ref={provided.innerRef}
                    isdraggingover={`${snapshot.isDraggingOver}`}
                  >
                    {data &&
                      data.map((d, i) => (
                        <Draggable key={d.rid} draggableId={`${d.rid}`} index={i}>
                          {(provided, snapshot) => (
                            <DraggableContainer
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                              isdragging={`${snapshot.isDragging}`}
                            >
                              <Grid container style={{ alignItems: "center" }}>
                                <Rule
                                  key={d.rid}
                                  ruleData={d}
                                  onModify={modifyRule}
                                  addRule={addRuleAtPosition}
                                  deleteRule={deleteRule}
                                />
                              </Grid>
                            </DraggableContainer>
                          )}
                        </Draggable>
                      ))}
                    {provided.placeholder}
                  </DroppableContainer>
                )}
              </Droppable>
              <Grid item xs={8} style={{ display: "flex", justifyContent: "left", alignItems: "center" }}>
                <Button variant="outlined" onClick={() => addRule(-1)}>
                  + New rule
                </Button>
              </Grid>
            </Grid>
          </DragDropContext>
          <CancelRulesDialog
            open={showDialog}
            onConfirm={() => onNavigateBack()}
            onClose={() => setShowDialog(false)}
          />
        </ContentPanel>
        <Footer>
          <Grid container spacing={1} alignItems="center">
            <Grid item xs={12} style={{ display: "flex", justifyContent: "right", alignItems: "right" }}>
              <div style={{ paddingRight: "24px" }}>
                <Button variant="outlined" onClick={onCancelButtonClick}>
                  Cancel
                </Button>
                &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                <Button variant="contained" onClick={onSaveButtonClick} disabled={!isEditable}>
                  Save as new version
                </Button>
              </div>
            </Grid>
          </Grid>
        </Footer>
        {!isEmpty(uploadResponse) && (
          <RuleErrorsDialog errors={uploadResponse} open={true} onClose={() => setUploadResponse([])} />
        )}
      </>
    );
  }
};
